import { useEffect, useState } from "react";
import { addDays, format, startOfWeek } from "date-fns";
import { BanIcon, Check, ChevronsUpDown, RotateCcw, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCreateShifts, useDeleteShift, useUpdateShift } from "@/hooks/useShifts";
import type { Shift, ShiftCreateInput } from "@/types/shift.type";
import type { UserType } from "@/types/user.type";
import { showErrorToast } from "../ui/toast";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: UserType[];
  shift?: Shift | null;
  defaultDate?: string; // YYYY-MM-DD
  defaultEmployeeId?: string;
  showDateField?: boolean;
};

const fullNameOf = (employee?: UserType | null) =>
  employee
    ? `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim()
    : "";

const formatDateLabel = (yyyyMmDd: string) =>
  new Date(`${yyyyMmDd}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/** Retourne les 7 jours (lun→dim) de la semaine contenant `dateStr` */
const getWeekDays = (dateStr: string): string[] => {
  const ref = new Date(`${dateStr}T12:00:00`);
  const monday = startOfWeek(ref, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) =>
    format(addDays(monday, i), "yyyy-MM-dd")
  );
};

export const ShiftFormDialog = ({
  open,
  onOpenChange,
  employees,
  shift,
  defaultDate,
  defaultEmployeeId,
  showDateField = false,
}: Props) => {
  const isEdit = !!shift;

  const { mutate: createMutate, isPending: isCreating } = useCreateShifts();
  const { mutate: updateMutate, isPending: isUpdating } = useUpdateShift();
  const { mutate: deleteMutate } = useDeleteShift();

  const isPending = isCreating || isUpdating;

  const [employeeId, setEmployeeId] = useState<string>("");

  // Mode création : liste des jours sélectionnés dans la semaine
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  // Les 7 jours de la semaine affichés
  const [weekDays, setWeekDays] = useState<string[]>([]);

  // Mode édition : date unique
  const [editDate, setEditDate] = useState<string>("");

  const [startTime, setStartTime] = useState<string>("09:00");
  const [endTime, setEndTime] = useState<string>("17:00");
  const [breakValue, setBreakValue] = useState<string>("0");
  const [breakUnit, setBreakUnit] = useState<"minutes" | "hours">("minutes");
  const [note, setNote] = useState<string>("");

  const [employeePopoverOpen, setEmployeePopoverOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (isEdit && shift) {
      setEmployeeId(shift.employeeId);
      const displayDate = shift.workDate
        ? format(new Date(shift.workDate), "yyyy-MM-dd")
        : format(new Date(shift.startTime), "yyyy-MM-dd");
      setEditDate(displayDate);
      setStartTime(format(new Date(shift.startTime), "HH:mm"));
      setEndTime(format(new Date(shift.endTime), "HH:mm"));
      const min = shift.breakMinutes ?? 0;
      if (min > 0 && min % 60 === 0) {
        setBreakValue(String(min / 60));
        setBreakUnit("hours");
      } else {
        setBreakValue(String(min));
        setBreakUnit("minutes");
      }
      setNote(shift.note ?? "");
    } else {
      setEmployeeId(defaultEmployeeId ?? "");
      const initialDate = defaultDate ?? format(new Date(), "yyyy-MM-dd");
      setWeekDays(getWeekDays(initialDate));
      setSelectedDates([initialDate]);
      setStartTime("09:00");
      setEndTime("17:00");
      setBreakValue("0");
      setBreakUnit("minutes");
      setNote("");
    }
  }, [open, isEdit, shift, defaultDate, defaultEmployeeId]);

  const toggleDay = (d: string) => {
    setSelectedDates((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  };

  const buildShiftPayload = (workDate: string): Omit<ShiftCreateInput, "employeeId"> => {
    const rawBreak = Number(breakValue) || 0;
    const breakInMinutes = breakUnit === "hours" ? rawBreak * 60 : rawBreak;

    const heure = parseInt(startTime.split(":")[0], 10);
    const isAfterMidnight = heure < 6;

    const startDateStr = isAfterMidnight
      ? format(addDays(new Date(workDate), 1), "yyyy-MM-dd")
      : workDate;

    const endDateStr =
      endTime <= startTime
        ? format(addDays(new Date(startDateStr), 1), "yyyy-MM-dd")
        : startDateStr;

    return {
      startTime: new Date(`${startDateStr}T${startTime}:00`).toISOString(),
      endTime: new Date(`${endDateStr}T${endTime}:00`).toISOString(),
      workDate,
      breakMinutes: breakInMinutes,
      note: note.trim() ? note.trim() : null,
    };
  };

  const handleSubmit = () => {
    if (!employeeId) return;

    if (isEdit && shift) {
      const payload = buildShiftPayload(editDate);
      updateMutate(
        {
          id: shift.id,
          data: {
            startTime: payload.startTime,
            endTime: payload.endTime,
            workDate: editDate,
            breakMinutes: payload.breakMinutes,
            note: payload.note,
          },
        },
        { onSuccess: () => onOpenChange(false) }
      );
      return;
    }

    if (selectedDates.length === 0) return;

    const handleCreateSuccess = (data: { created: number }) => {
      if (data.created > 0) onOpenChange(false);
    };

    // Trier les dates sélectionnées dans l'ordre de la semaine
    const orderedDates = weekDays.filter((d) => selectedDates.includes(d));

    if (orderedDates.length === 1) {
      createMutate(
        { employeeId, ...buildShiftPayload(orderedDates[0]) },
        { onSuccess: handleCreateSuccess }
      );
    } else {
      const shiftsPayload: ShiftCreateInput[] = orderedDates.map((d) => ({
        employeeId,
        ...buildShiftPayload(d),
      }));
      createMutate(
        { shifts: shiftsPayload },
        { onSuccess: handleCreateSuccess }
      );
    }
  };

  const handleDelete = () => {
    deleteMutate(shift?.id ?? "", {
      onSuccess: () => onOpenChange(false),
      onError: (error) => showErrorToast(error.message),
    });
  };

  const handleToggleStatus = () => {
    if (!shift) return;
    const newStatus = shift.status === "CANCELLED" ? "PLANNED" : "CANCELLED";
    updateMutate(
      { id: shift.id, data: { status: newStatus } },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  const selectedEmployee = employees.find((e) => e.id === employeeId);

  const editingEmployee =
    isEdit && shift
      ? employees.find((e) => e.id === (employeeId ?? shift.employeeId)) ??
        shift.user ??
        null
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Modifier le créneau" : "Créer un créneau"}
            {isEdit && shift?.status === "CANCELLED" && (
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                Annulé
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? [
                  editingEmployee && fullNameOf(editingEmployee),
                  editDate && `Pour le ${formatDateLabel(editDate)}`,
                ]
                  .filter(Boolean)
                  .join(" — ") || null
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Sélection de l'employé (1 seul) */}
          {!isEdit && (
            <div className="space-y-2">
              <Label>Employé</Label>
              <Popover open={employeePopoverOpen} onOpenChange={setEmployeePopoverOpen}>
                <PopoverTrigger asChild>
                  <div
                    role="combobox"
                    aria-expanded={employeePopoverOpen}
                    className={cn(
                      "flex min-h-9 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs",
                      "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
                      "hover:bg-accent/30 transition-colors"
                    )}
                  >
                    <span className={cn("flex-1", !selectedEmployee && "text-muted-foreground")}>
                      {selectedEmployee
                        ? fullNameOf(selectedEmployee)
                        : "Sélectionner un employé"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Rechercher un employé..." />
                    <CommandList>
                      {employees.length === 0 ? (
                        <CommandEmpty>Aucun employé.</CommandEmpty>
                      ) : (
                        <>
                          <CommandEmpty>Aucun résultat.</CommandEmpty>
                          {employees.map((employee) => {
                            const name = fullNameOf(employee);
                            const isSelected = employeeId === employee.id;
                            return (
                              <CommandItem
                                key={employee.id}
                                value={name}
                                onSelect={() => {
                                  setEmployeeId(employee.id!);
                                  setEmployeePopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    isSelected ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {name}
                              </CommandItem>
                            );
                          })}
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Sélection des jours de la semaine (mode création) */}
          {!isEdit && weekDays.length === 7 && (
            <div className="space-y-2">
              <Label>
                Jours de la semaine
                {selectedDates.length > 1 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({selectedDates.length} jours sélectionnés)
                  </span>
                )}
              </Label>
              <div className="grid grid-cols-7 gap-1">
                {weekDays.map((d) => {
                  const dayDate = new Date(`${d}T12:00:00`);
                  const dayName = dayDate.toLocaleDateString("fr-FR", { weekday: "short" });
                  const dayNum = dayDate.getDate();
                  const isSelected = selectedDates.includes(d);

                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(d)}
                      className={cn(
                        "flex flex-col items-center justify-center rounded-md border py-2 text-xs font-medium transition-colors",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-transparent text-foreground hover:bg-accent/50"
                      )}
                    >
                      <span className="capitalize">{dayName.replace(".", "")}</span>
                      <span className={cn("text-base font-semibold leading-tight", isSelected ? "text-primary-foreground" : "text-foreground")}>
                        {dayNum}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date unique en mode édition */}
          {isEdit && showDateField && (
            <div className="space-y-2">
              <Label htmlFor="editDate">Date</Label>
              <Input
                id="editDate"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
              />
            </div>
          )}

          {/* Heures */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="startTime">Début</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">Fin</Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
          {endTime <= startTime && startTime !== "" && endTime !== "" && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <span>⚠️</span>
              Ce créneau se termine le lendemain à {endTime}
            </p>
          )}

          {/* Pause */}
          <div className="space-y-2">
            <Label htmlFor="breakValue">
              Pause{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (déduite du temps de travail entre début et fin)
              </span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="breakValue"
                type="number"
                min="0"
                step={breakUnit === "hours" ? "0.5" : "5"}
                value={breakValue}
                onChange={(e) => setBreakValue(e.target.value)}
                className="flex-1"
              />
              <Select
                value={breakUnit}
                onValueChange={(v) => setBreakUnit(v as "minutes" | "hours")}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Heures</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Note (optionnel)</Label>
            <Input
              id="note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: remplacement, formation..."
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-2">
          <div className="flex items-center gap-2">
            {isEdit && shift && (
              <>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>

                {shift.status === "CANCELLED" ? (
                  <Button
                    variant="outline"
                    onClick={handleToggleStatus}
                    disabled={isPending}
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Réactiver
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleToggleStatus}
                    disabled={isPending}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <BanIcon className="h-4 w-4 mr-2" />
                    Annuler le créneau
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isPending ||
                !employeeId ||
                (!isEdit && selectedDates.length === 0)
              }
            >
              {isPending
                ? isEdit
                  ? "Enregistrement..."
                  : "Création..."
                : isEdit
                ? "Enregistrer"
                : selectedDates.length > 1
                ? `Créer (${selectedDates.length} jours)`
                : "Créer"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
